import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    CardPartContentProps,
    CardContextType,
    CardPartLayout,
    CardPartKind
} from "./type";
import {
    CARD_DEFAULT_LAYOUT
} from "./util";
import {
    warnOnce
} from "../../utils/development";

export const CARD_PART_NAMES: Record<CardPartKind, string> = {
    description: "Description",
    content: "Content",
    action: "Action",
    footer: "Footer",
    header: "Header",
    title: "Title"
};

export const CardContext = createContext<CardContextType | undefined>(undefined);

export const CardLayoutContext = createContext<CardPartLayout>(CARD_DEFAULT_LAYOUT);

export const CardContentFlushContext = createContext(false);

export const useCardContext = ({
    part
}: {
    part: CardPartKind;
}): CardContextType | undefined => {
    const context = useContext(CardContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Card.${CARD_PART_NAMES[part]}: must be rendered inside <Card>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withCardContext = <P extends object>(
    part: CardPartKind,
    Content: ComponentType<CardPartContentProps<P>>
): FC<P> & {
    cardPart: CardPartKind;
} => {
    const CardPartBase: FC<P> = (props) => {
        const context = useCardContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    CardPartBase.displayName = `Card.${CARD_PART_NAMES[part]}`;

    return Object.assign(CardPartBase, {
        cardPart: part
    });
};
